import { useCallback, useRef } from 'react'

import { useLazyGetStructureTreeQuery } from '@/rtkApi/mddApiSliceGeneratedV2'
import { logError } from '@/utils/errorHandling'

import { CONTAINER_TYPES, toTreeItem } from '../constants'
import { StructureTreeItem, UseTreeSelectionOptions, UseTreeSelectionReturn } from '../types'

const groupByParent = (items: { id?: number; parent?: { id?: number } }[]): Record<string, StructureTreeItem[]> =>
	items.reduce((acc, item) => {
		const parentId = String(item.parent?.id)
		if (!acc[parentId]) acc[parentId] = []
		acc[parentId].push(toTreeItem(item))
		return acc
	}, {} as Record<string, StructureTreeItem[]>)

export const useTreeSelection = ({
	checkedItemIds,
	onCheckedItemsChange,
	onChildrenLoaded,
	childrenMap,
	setChildrenMap,
	setUsedIds,
}: UseTreeSelectionOptions): UseTreeSelectionReturn => {
	const [fetchStructureTree] = useLazyGetStructureTreeQuery()
	const descendantsCache = useRef<Map<number, number[]>>(new Map())

	const getDescendants = useCallback(
		(parentId: string): number[] => {
			const children = childrenMap[parentId]
			if (!children) return []
			return children.flatMap((child) => [child.structureId, ...getDescendants(child.id)])
		},
		[childrenMap],
	)

	const loadAndCacheDescendants = useCallback(
		async (parentId: number): Promise<number[]> => {
			const cached = descendantsCache.current.get(parentId)
			if (cached) return cached

			try {
				const result = await fetchStructureTree({ structureId: parentId }).unwrap()
				const ids = result.map((item) => item.id!)
				descendantsCache.current.set(parentId, ids)

				const grouped = groupByParent(result)
				const allIds = result.map((item) => String(item.id))
				const parentIds = new Set(Object.keys(grouped))
				const itemTypeById = new Map(result.map((item) => [String(item.id), item.type]))
				const leafIds = allIds.filter((id) => {
					if (parentIds.has(id)) return false
					const itemType = itemTypeById.get(id)
					return !CONTAINER_TYPES.includes(itemType || '')
				})

				setUsedIds((prev) => new Set([...prev, ...allIds]))
				setChildrenMap((prev) => {
					const updated = { ...prev }
					Object.entries(grouped).forEach(([pid, newChildren]) => {
						const existing = updated[pid] || []
						const existingIds = new Set(existing.map((c) => c.id))
						updated[pid] = [...existing, ...newChildren.filter((c) => !existingIds.has(c.id))]
					})
					leafIds.forEach((id) => {
						if (updated[id] === undefined) updated[id] = []
					})
					return updated
				})

				if (onChildrenLoaded) {
					Object.entries(grouped).forEach(([pid, children]) => {
						onChildrenLoaded(Number(pid), children)
					})
				}

				return ids
			} catch (error) {
				logError(error, { operation: 'useTreeSelection.loadAndCacheDescendants', details: { parentId } })
				return []
			}
		},
		[fetchStructureTree, setChildrenMap, setUsedIds, onChildrenLoaded],
	)

	const getDescendantsFromCache = useCallback(
		(parentId: number): number[] => descendantsCache.current.get(parentId) ?? getDescendants(String(parentId)),
		[getDescendants],
	)

	const handleCheckChange = useCallback(
		async (itemId: number, checked: boolean, itemType: string, isCurrentlyIndeterminate: boolean) => {
			if (!onCheckedItemsChange) return

			if (itemType === 'SYSTEM') {
				if (isCurrentlyIndeterminate) {
					const descendantIds = getDescendantsFromCache(itemId)
					const allIds = [itemId, ...descendantIds]
					onCheckedItemsChange(checkedItemIds.filter((id) => !allIds.includes(id)))
					return
				}
				if (checked) {
					onCheckedItemsChange([...checkedItemIds, itemId])
					return
				}
			}

			const descendantIds = checked ? await loadAndCacheDescendants(itemId) : getDescendantsFromCache(itemId)
			const allIds = [itemId, ...descendantIds]

			onCheckedItemsChange(
				checked ? [...new Set([...checkedItemIds, ...allIds])] : checkedItemIds.filter((id) => !allIds.includes(id)),
			)
		},
		[checkedItemIds, onCheckedItemsChange, loadAndCacheDescendants, getDescendantsFromCache],
	)

	const isIndeterminate = useCallback(
		(itemId: string): boolean => {
			const descendants = getDescendants(itemId)
			if (descendants.length === 0) return false

			const isChecked = checkedItemIds.includes(Number(itemId))
			const checkedCount = descendants.filter((id) => checkedItemIds.includes(id)).length

			return (!isChecked && checkedCount > 0) || (isChecked && checkedCount > 0 && checkedCount < descendants.length)
		},
		[getDescendants, checkedItemIds],
	)

	return { getCachedDescendantIds: getDescendants, handleCheckChange, isIndeterminate }
}
